<template>
  <figure :class="classes" tabindex="0" :aria-label="data.caption" class="image-figure">
    <AppImageFocal :data="computedImageData" :renditions="renditions" :picture-classes="computedPictureClasses"/>
    <button
        v-if="props.data.image"
        class="image-figure__zoom-btn"
        type="button"
        aria-label="Afbeelding vergroten"
        @click.prevent="openLightbox()"
    >
      <AppIconLoader name="largen" variant="medium" aria-hidden="true"/>
    </button>

    <AppIntersectionObserver :options="intersectionOptions" @intersect="onIntersect">
      <figcaption class="image-figure__caption" :class="{'image-figure__caption--show': state.show}">
        <p class="image-figure__text" :aria-label="`bijschrift: ${data.caption}`">{{ data.caption }}</p>
        <small class="image-figure__small" :aria-label="`bron: ${data.source}`">{{ data.source }}</small>
      </figcaption>
    </AppIntersectionObserver>
  </figure>
</template>

<script setup lang="ts">
import {computed, reactive} from 'vue'
import {TImageBlock} from '~/types/block-types'
import {TImageRendition} from '~/types/other-types'
import {useImageFocal} from "~/composables/useImageFocal";
import {useRuntimeConfig} from "nuxt/app";

const config = useRuntimeConfig()
const baseURL = config.public.apiBaseUrl

const { open } = useLightbox();

const props = defineProps<{
  data: TImageBlock,
  renditions: TImageRendition[]
  classes?: String
  captionClasses?: String
}>()

const state = reactive({
  show: false
})

const intersectionOptions = {
  threshold: '0.5'
}

const onIntersect = (data) => {
  state.show = data.visible
}

const computedImageData = computed(() => {

  // override the copyright data with the data from the component itself (used for archive page logic only)
  if (props.data.copyright && props.data.image) {
    const imageData = {...props.data.image}
    imageData.image.copyright = props.data.copyright
    return imageData
  }

  return props.data.image
})

const openLightbox = () => {

  const lastRendition = props.renditions?.at(-1)

  if (lastRendition && props.data.image) {
    const twoX = useImageFocal(props.data.image.image, lastRendition.width !== 'auto' ? Math.ceil(lastRendition.width * 2.5) : 'auto', lastRendition.width !== 'auto' ? Math.ceil(lastRendition.height * 2.5) : 'auto', props.data.image.focalPoints[0], 'webp')
    const url = `${baseURL}/assets/${twoX.value}`
    open({ src: url, alt: props.data.image.image.description || 'AVROTROS' });
  }
};

const computedPictureClasses = computed(() => {
  if (props.data.contain) {
    return props.data.orientation === 'landscape' ? 'image-focal__picture--contain-landscape' : 'image-focal__picture--contain-portrait'
  }

  return ''
})
</script>

<style scoped lang="postcss">
.image-figure {
  position: relative;

  &--full-width {
    width: 100%;
  }

  &__zoom-btn {
    right: 0.5rem;
    top: 0.5rem;
    position: absolute;

    @media (--sm) {
      display: none;
    }

    @media (--md) {
      right: 0.75rem;
      top: 0.75rem;
    }

    @media (--lg) {
      right: 1rem;
      top: 1rem;
    }
  }

  &__caption {
    padding-top: 0.5rem;

    @media (--lg) {
      padding-top: 1rem;
    }
  }

  &__text {
    @mixin app-caption-wide;
  }

  &__small {
    display: block;
    @mixin app-caption;
  }
}
</style>
